/**
 * Decision Panel Component
 * Shows routing decision results with metrics and reasoning
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, ChevronDown, ChevronUp, RefreshCw, Check, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { RoutingDecision, PromptFeatures } from '@/lib/types';
import { generateHash } from '@/lib/demo/routing-engine';
import { fadeInVariants, slideUpVariants } from '@/components/animations/variants';

interface DecisionPanelProps {
  decision: RoutingDecision | null;
  features: PromptFeatures | null;
  isLoading: boolean;
  originalPrompt: string;
  onReset: () => void;
}

export function DecisionPanel({
  decision,
  features,
  isLoading,
  originalPrompt,
  onReset,
}: DecisionPanelProps) {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="backdrop-blur-md bg-background/50 border border-border/50 rounded-xl p-6 shadow-xl">
      <h3 className="text-xl font-semibold mb-4">Routing Decision</h3>

      <AnimatePresence mode="wait">
        {/* Empty State */}
        {!decision && !isLoading && (
          <EmptyState key="empty" />
        )}

        {/* Loading State */}
        {isLoading && (
          <LoadingState key="loading" />
        )}

        {/* Results State */}
        {decision && !isLoading && (
          <ResultsState
            key="results"
            decision={decision}
            features={features}
            originalPrompt={originalPrompt}
            showDetails={showDetails}
            onToggleDetails={() => setShowDetails(!showDetails)}
            onReset={onReset}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function EmptyState() {
  return (
    <motion.div
      className="text-center py-12"
      variants={fadeInVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      <Brain className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
      <p className="text-muted-foreground">
        Enter a prompt and click "Analyze" to see intelligent routing in action.
      </p>
    </motion.div>
  );
}

function LoadingState() {
  return (
    <motion.div
      className="py-8"
      variants={fadeInVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      {/* Animated gradient bar */}
      <div className="h-4 rounded-full bg-gradient-to-r from-primary to-accent mb-6 overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-transparent via-white/20 to-transparent"
          animate={{
            x: ['-100%', '200%'],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      </div>

      {/* Skeleton lines */}
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <motion.div
            key={i}
            className="h-4 bg-muted rounded"
            style={{ width: i === 3 ? '50%' : i === 2 ? '75%' : '100%' }}
            animate={{
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: i * 0.2,
            }}
          />
        ))}
      </div>

      <p className="text-center text-muted-foreground mt-4">
        Analyzing prompt features...
      </p>
    </motion.div>
  );
}

function ResultsState({
  decision,
  features,
  originalPrompt,
  showDetails,
  onToggleDetails,
  onReset,
}: {
  decision: RoutingDecision;
  features: PromptFeatures | null;
  originalPrompt: string;
  showDetails: boolean;
  onToggleDetails: () => void;
  onReset: () => void;
}) {
  return (
    <motion.div
      variants={slideUpVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      {/* Selected Model */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
            <Cpu className="w-4 h-4" />
          </div>
          <div>
            <div className="font-semibold">{decision.selectedModel.name}</div>
            <div className="text-sm text-muted-foreground">
              {decision.selectedModel.provider}
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <MetricBox
          label="Confidence"
          value={`${Math.round(decision.confidence * 100)}%`}
          variant="success"
        />
        <MetricBox
          label="Est. Cost"
          value={`$${decision.estimatedCost.toFixed(3)}/1M`}
        />
        <MetricBox
          label="Est. Latency"
          value={`${Math.round(decision.estimatedLatency)}ms`}
        />
        <MetricBox
          label="Savings"
          value={`${decision.savings.vsAlwaysPremium}%`}
          variant="success"
        />
      </div>

      {/* Reasoning */}
      <div className="mb-6">
        <h4 className="font-semibold mb-3">Reasoning:</h4>
        <div className="space-y-2">
          {decision.reasoning.map((reason, index) => (
            <div key={index} className="flex items-start space-x-2 text-sm">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>{reason}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex space-x-3">
        <Button
          variant="outline"
          className="flex-1"
          onClick={onToggleDetails}
        >
          {showDetails ? (
            <>
              <ChevronUp className="w-4 h-4 mr-2" />
              Hide Details
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 mr-2" />
              View Details
            </>
          )}
        </Button>
        <Button variant="outline" size="icon" onClick={onReset}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Expandable Details */}
      <AnimatePresence>
        {showDetails && features && (
          <motion.div
            className="mt-6 pt-6 border-t border-border"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h4 className="font-semibold mb-3">Extracted Features:</h4>
            <div className="space-y-2 text-sm">
              <DetailRow label="Complexity Score:" value={features.complexityScore.toFixed(2)} />
              <DetailRow label="Token Count:" value={features.tokenCount.toString()} />
              <DetailRow label="Task Type:" value={features.taskType} />
              <DetailRow
                label="SHA-256 Hash:"
                value={`${generateHash(originalPrompt).substring(0, 16)}...`}
                mono
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function MetricBox({
  label,
  value,
  variant = 'default',
}: {
  label: string;
  value: string;
  variant?: 'default' | 'success';
}) {
  return (
    <div>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div
        className={`text-2xl font-bold ${
          variant === 'success' ? 'text-green-500' : ''
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span className={mono ? 'font-mono text-xs' : ''}>{value}</span>
    </div>
  );
}
